import { API_BASE_URL } from '../config/environment';
import type { AdminDashboard, AdminUser } from '../types/admin';
import type { Board, BoardSettings, Card, CardGroup, Column } from '../types/board';
import type { Feedback, FeedbackReviewStatus } from '../types/feedback';

interface AdminErrorResponse {
  error?: unknown;
  message?: unknown;
}

export class AdminApiService {
  async getDashboard(sessionToken: string): Promise<AdminDashboard> {
    const response = await fetch(`${API_BASE_URL}/admin/dashboard`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch((): AdminErrorResponse => ({}));
      throw new Error(getErrorMessage(errorData, response.status));
    }

    const data: unknown = await response.json();
    if (!isAdminDashboard(data)) {
      throw new Error('Invalid admin dashboard response');
    }
    return data;
  }

  async deleteBoard(sessionToken: string, boardId: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/admin/boards/${encodeURIComponent(boardId)}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch((): AdminErrorResponse => ({}));
      throw new Error(getErrorMessage(errorData, response.status));
    }

    const data: unknown = await response.json();
    if (!isDeleteBoardResponse(data)) {
      throw new Error('Invalid delete board response');
    }
    return data.boardId;
  }

  async getFeedback(sessionToken: string): Promise<Feedback[]> {
    const response = await fetch(`${API_BASE_URL}/admin/feedback`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch((): AdminErrorResponse => ({}));
      throw new Error(getErrorMessage(errorData, response.status));
    }

    const data: unknown = await response.json();
    if (!isFeedbackListResponse(data)) {
      throw new Error('Invalid admin feedback response');
    }
    return data.feedback;
  }

  async reviewFeedback(
    sessionToken: string,
    feedbackId: string,
    status: FeedbackReviewStatus
  ): Promise<Feedback> {
    const response = await fetch(`${API_BASE_URL}/admin/feedback/${encodeURIComponent(feedbackId)}/status`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch((): AdminErrorResponse => ({}));
      throw new Error(getErrorMessage(errorData, response.status));
    }

    const data: unknown = await response.json();
    if (!isFeedbackResponse(data)) {
      throw new Error('Invalid feedback review response');
    }
    return data.feedback;
  }
}

function getErrorMessage(errorData: AdminErrorResponse, status: number): string {
  if (typeof errorData.error === 'string') return errorData.error;
  if (typeof errorData.message === 'string') return errorData.message;
  return `Admin request failed (${status})`;
}

function isAdminDashboard(value: unknown): value is AdminDashboard {
  if (!isRecord(value)) return false;
  return Array.isArray(value.boards)
    && value.boards.every(isBoard)
    && Array.isArray(value.users)
    && value.users.every(isAdminUser);
}

function isAdminUser(value: unknown): value is AdminUser {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.name === 'string'
    && (value.role === 'admin' || value.role === 'user')
    && value.authProvider === 'google'
    && typeof value.createdAt === 'string'
    && typeof value.updatedAt === 'string'
    && (value.email === undefined || typeof value.email === 'string')
    && (value.pictureUrl === undefined || typeof value.pictureUrl === 'string');
}

function isDeleteBoardResponse(value: unknown): value is { boardId: string } {
  return isRecord(value) && typeof value.boardId === 'string';
}

function isFeedbackListResponse(value: unknown): value is { feedback: Feedback[] } {
  return isRecord(value)
    && Array.isArray(value.feedback)
    && value.feedback.every(isFeedback);
}

function isFeedbackResponse(value: unknown): value is { feedback: Feedback } {
  return isRecord(value) && isFeedback(value.feedback);
}

function isFeedback(value: unknown): value is Feedback {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.message === 'string'
    && (value.email === undefined || typeof value.email === 'string')
    && (value.status === 'pending' || value.status === 'accepted' || value.status === 'rejected')
    && typeof value.createdAt === 'string'
    && typeof value.updatedAt === 'string'
    && (value.reviewedAt === undefined || typeof value.reviewedAt === 'string')
    && (value.reviewedBy === undefined || typeof value.reviewedBy === 'string');
}

function isBoard(value: unknown): value is Board {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.name === 'string'
    && (value.adminId === undefined || typeof value.adminId === 'string')
    && Array.isArray(value.columns)
    && value.columns.every(isColumn)
    && isBoardSettings(value.settings)
    && typeof value.hasPassword === 'boolean'
    && typeof value.hasAdminPassword === 'boolean'
    && typeof value.createdAt === 'string'
    && (value.updatedAt === undefined || typeof value.updatedAt === 'string')
    && (value.version === undefined || typeof value.version === 'number');
}

function isBoardSettings(value: unknown): value is BoardSettings {
  if (!isRecord(value)) return false;
  return (value.cardVisibility === 'VISIBLE' || value.cardVisibility === 'BLURRED')
    && typeof value.maxColumns === 'number'
    && typeof value.maxCardsPerColumn === 'number'
    && typeof value.showAuthorForParticipants === 'boolean'
    && typeof value.showAuthorForAdmin === 'boolean'
    && typeof value.showCardDate === 'boolean'
    && typeof value.enableMergeCards === 'boolean'
    && typeof value.enableCardGroups === 'boolean'
    && typeof value.requireNameOnJoin === 'boolean'
    && typeof value.allowAnonymous === 'boolean';
}

function isColumn(value: unknown): value is Column {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.name === 'string'
    && typeof value.color === 'string'
    && typeof value.order === 'number'
    && Array.isArray(value.cards)
    && value.cards.every(isCard)
    && Array.isArray(value.groups)
    && value.groups.every(isCardGroup);
}

function isCard(value: unknown): value is Card {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.content === 'string'
    && typeof value.author === 'string'
    && (value.createdBy === undefined || typeof value.createdBy === 'string')
    && typeof value.columnId === 'string'
    && (value.groupId === undefined || typeof value.groupId === 'string')
    && typeof value.createdAt === 'string'
    && (value.updatedAt === undefined || typeof value.updatedAt === 'string');
}

function isCardGroup(value: unknown): value is CardGroup {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.name === 'string'
    && (value.description === undefined || typeof value.description === 'string')
    && typeof value.color === 'string'
    && typeof value.order === 'number';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export const adminApi = new AdminApiService();
